import { useEffect, useLayoutEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import styles from "./StreakToast.module.css";

interface StreakToastProps {
  from: number;
  to: number;
  badgeRef: React.RefObject<HTMLElement | null>;
}

type Phase = "start" | "center" | "counting" | "hold" | "return" | "settled" | "done";

interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Target for the badge (start/return) state: the point the pill's center should
 * sit on, plus the scale that shrinks the full-size pill to badge height. Y is
 * clamped so a scrolled-away header still targets the top edge rather than off
 * screen above it.
 */
export function badgeTarget(rect: Rect, toastHeight: number) {
  const x = rect.left + rect.width / 2;
  const y = Math.max(rect.height / 2, rect.top + rect.height / 2);
  const s = toastHeight > 0 ? rect.height / toastHeight : 0.5;
  return { x, y, s };
}

/** Target for the centered state. */
export function centerTarget(viewportW: number, viewportH: number) {
  return { x: viewportW / 2, y: viewportH * 0.4 };
}

export function StreakToast({ from, to, badgeRef }: StreakToastProps) {
  const [phase, setPhase] = useState<Phase>("start");
  const [display, setDisplay] = useState(from);
  const moverRef = useRef<HTMLDivElement>(null);
  const toastRef = useRef<HTMLDivElement>(null);

  // Measure the badge + viewport and write the position custom properties onto
  // the mover. The mover's origin (transform-origin: 0 0) is the point placed at
  // (x, y); scale pivots on that origin, so the pill's center lands on the target
  // regardless of the pill's label-dependent, animating width.
  const syncPositions = (label: string): void => {
    const mover = moverRef.current;
    const badge = badgeRef.current;
    if (!mover || !badge) {
      if (import.meta.env.PROD) {
        Sentry.captureMessage(`StreakToast: null ref at ${label}`, {
          level: "warning",
          extra: { label, hasBadge: !!badge, hasMover: !!mover },
        });
      }
      return;
    }
    const r = badge.getBoundingClientRect();
    const toastH = toastRef.current?.getBoundingClientRect().height ?? 0;
    const badgePos = badgeTarget(r, toastH);
    const centerPos = centerTarget(window.innerWidth, window.innerHeight);
    mover.style.setProperty("--badge-x", `${badgePos.x}px`);
    mover.style.setProperty("--badge-y", `${badgePos.y}px`);
    mover.style.setProperty("--badge-scale", `${badgePos.s}`);
    mover.style.setProperty("--center-x", `${centerPos.x}px`);
    mover.style.setProperty("--center-y", `${centerPos.y}px`);
  };

  // Hide the real badge during animation, restore when done
  useEffect(() => {
    if (from >= to || to <= 1) return;
    const badge = badgeRef.current;
    if (badge) badge.style.visibility = "hidden";
    return () => { if (badge) badge.style.visibility = ""; };
  }, [from, to, badgeRef]);

  // Position before the first paint of the "start" render (the element appears
  // after returning null during "done"), so it starts exactly on the badge.
  useLayoutEffect(() => {
    if (from >= to || to <= 1 || phase !== "start") return;
    syncPositions("start");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, phase]);

  useEffect(() => {
    if (from >= to || to <= 1) { setPhase("done"); return; }
    setPhase("start");
    // Wait one frame so the "start" class (transition: none) applies, then fly.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setPhase("center"));
    });
  }, [from, to]);

  // Phase transitions
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    switch (phase) {
      case "center":
        timer = setTimeout(() => setPhase("counting"), 600);
        break;
      case "counting":
        (() => {
          let current = from;
          const step = () => {
            current++;
            setDisplay(current);
            if (current < to) {
              timer = setTimeout(step, 600);
            } else {
              timer = setTimeout(() => setPhase("hold"), 800);
            }
          };
          timer = setTimeout(step, 600);
        })();
        break;
      case "hold":
        // Re-measure in case the badge shifted (scroll/resize) during the fly-in.
        syncPositions("hold");
        timer = setTimeout(() => setPhase("return"), 100);
        break;
      case "return":
        timer = setTimeout(() => setPhase("settled"), 700);
        break;
    }
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  if (from >= to || to <= 1 || phase === "done") return null;

  const posClass = phase === "start" ? styles.atBadgeStart
    : (phase === "return" || phase === "settled") ? styles.atBadgeReturn
    : styles.atCenter;

  return (
    <div ref={moverRef} className={`${styles.mover} ${posClass}`} data-testid="streak-mover">
      <div ref={toastRef} className={styles.toast} data-testid="streak-pill">
        <span className={styles.icon}>{"🔥"}</span>
        <span className={styles.count} key={display}>{display}</span>
        <span className={styles.label}>Day Streak</span>
      </div>
    </div>
  );
}
